import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDatabase, ref as dbRef, onValue } from "firebase/database";
import Reveal from "../../components/reveal/Reveal";
import "./stories.css";

const FETCH_TIMEOUT = 9000;

/* Roughly half the archive is written in Bangla. Marking which is which is
   derived from the text itself, not guessed at. */
const BENGALI = /[ঀ-৿]/;

const normalise = ([key, post]) => ({
  ...post,
  key,
  sections: post.sections || [
    { images: post.images || [], text: post.text || "" },
  ],
});

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const excerpt = (text, max = 190) => {
  if (!text) return "";
  const clean = String(text).replace(/<[^>]+>/g, "").trim();
  return clean.length > max ? `${clean.slice(0, max).trimEnd()}…` : clean;
};

const titleOf = (post) => post.title || post.heading || "Untitled story";

/* The lead story carries the photograph. If Storage is unreachable the cover
   simply drops out and the lead becomes a large text entry — no placeholder
   tile pretending to be a picture. */
const LeadCover = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <div className="story-lead__media">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

const Stories = () => {
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let settled = false;

    const timer = window.setTimeout(() => {
      if (!settled) setStatus("unavailable");
    }, FETCH_TIMEOUT);

    let unsubscribe = () => {};
    try {
      unsubscribe = onValue(
        dbRef(getDatabase(), "blogs"),
        (snapshot) => {
          settled = true;
          const data = snapshot.val();
          if (!data) {
            setPosts([]);
            setStatus("empty");
            return;
          }
          setPosts(
            Object.entries(data)
              .map(normalise)
              .sort((a, b) => (a.key < b.key ? 1 : -1))
          );
          setStatus("ready");
        },
        () => {
          settled = true;
          setStatus("unavailable");
        }
      );
    } catch {
      settled = true;
      setStatus("unavailable");
    }

    return () => {
      window.clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  const [lead, ...rest] = posts;

  return (
    <section className="section stories" id="blog" aria-labelledby="stories-title">
      <div className="shell">
        <p className="marker">
          <span className="marker__num">04</span>
          <span>Stories</span>
        </p>

        <div className="stories__head">
          <h2 id="stories-title" className="t-h2 stories__title">
            What happened, and who it reached
          </h2>
          <p className="t-lead stories__intro">
            Reports from our programmes — written up as they happen, in English
            and Bangla.
          </p>
        </div>

        {status === "loading" ? (
          <div className="stories__skeleton" aria-busy="true">
            <div className="skeleton stories__sk-lead" />
            <div className="skeleton stories__sk-row" />
            <div className="skeleton stories__sk-row" />
            <div className="skeleton stories__sk-row" />
          </div>
        ) : null}

        {status === "ready" && lead ? (
          <>
            <Reveal as="article" className="story-lead" distance={20}>
              <LeadCover
                src={lead.sections[0]?.images?.[0]}
                alt={`Photograph from “${titleOf(lead)}”`}
              />
              <div className="story-lead__text">
                <p className="story__stamp">
                  <span>{formatDate(lead.date) || "Latest"}</span>
                  <span aria-hidden="true">/</span>
                  <span>{BENGALI.test(titleOf(lead)) ? "বাংলা" : "English"}</span>
                </p>
                <h3 className="t-display-2 story-lead__title">
                  <Link to={`/blogpost/${lead.key}`} className="story__link">
                    {titleOf(lead)}
                  </Link>
                </h3>
                <p className="t-body story-lead__excerpt">
                  {excerpt(lead.sections[0]?.text || lead.text)}
                </p>
                <span className="link-arrow" aria-hidden="true">
                  Read the story
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M2 8h12m0 0-4.5-4.5M14 8l-4.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </Reveal>

            {rest.length ? (
              <Reveal
                as="ol"
                className="stories__digest"
                stagger={60}
                distance={14}
              >
                {rest.map((post) => (
                  <li className="digest" key={post.key}>
                    <span className="digest__date">
                      {formatDate(post.date) || "—"}
                    </span>
                    <h3 className="digest__title">
                      <Link to={`/blogpost/${post.key}`} className="story__link">
                        {titleOf(post)}
                      </Link>
                    </h3>
                    <span className="digest__lang">
                      {BENGALI.test(titleOf(post)) ? "বাংলা" : "EN"}
                    </span>
                  </li>
                ))}
              </Reveal>
            ) : null}
          </>
        ) : null}

        {status === "empty" ? (
          <div className="stories__state">
            <h3 className="t-h3">No stories published yet</h3>
            <p className="t-body">
              Programme reports are posted here as they are written. Check back
              soon, or write to us if you want to hear about our work directly.
            </p>
            <a
              className="btn-sales btn-sales--ghost"
              href="mailto:salesbarishal1988@gmail.com"
            >
              Email SALES
            </a>
          </div>
        ) : null}

        {status === "unavailable" ? (
          <div className="stories__state" role="status">
            <h3 className="t-h3">Stories are not loading right now</h3>
            <p className="t-body">
              We could not reach the story archive. Reload the page to try
              again — everything else on this site still works.
            </p>
            <button
              type="button"
              className="btn-sales btn-sales--ghost"
              onClick={() => window.location.reload()}
            >
              Reload the page
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default Stories;
